import { useState, useMemo, useEffect, useRef } from 'react';
import { useEmailThread } from 'src/api/communications/email-threads/useEmailThread';
import type { EmailThreadEmail } from '@erp/rpc';

export interface UseEmailThreadViewerOptions {
  conversationId: string | null;
  onEmailSelect?: (email: EmailThreadEmail) => void;
  /**
   * Always fetch split-child documents, regardless of the per-email toggle.
   * Used when the caller needs to locate a specific document that may be a
   * split child (e.g. an AP invoice's source document).
   */
  forceIncludeSplitDocuments?: boolean;
}

export function useEmailThreadViewer({
  conversationId,
  onEmailSelect,
  forceIncludeSplitDocuments = false,
}: UseEmailThreadViewerOptions) {
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());
  // Emails whose split-child documents section is open. The thread query is
  // refetched with includeSplitDocuments whenever at least one is open.
  const [splitDocsExpandedEmails, setSplitDocsExpandedEmails] = useState<
    Set<string>
  >(new Set());
  const initializedForConversationRef = useRef<string | null>(null);

  // Reset split-doc expansion when the conversation changes so a newly
  // opened thread isn't fetched with includeSplitDocuments from stale state.
  const splitDocsConversationRef = useRef<string | null>(conversationId);
  let activeSplitDocsExpandedEmails = splitDocsExpandedEmails;
  if (splitDocsConversationRef.current !== conversationId) {
    splitDocsConversationRef.current = conversationId;
    activeSplitDocsExpandedEmails = new Set();
    if (splitDocsExpandedEmails.size > 0) {
      setSplitDocsExpandedEmails(new Set());
    }
  }

  const includeSplitDocuments =
    forceIncludeSplitDocuments || activeSplitDocsExpandedEmails.size > 0;

  // Fetch thread data
  const {
    data: threadData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useEmailThread(conversationId, includeSplitDocuments);

  // Get selected email
  const selectedEmail = useMemo(() => {
    if (!threadData?.emails || !selectedEmailId) return null;
    return (
      threadData.emails.find((email) => email.id === selectedEmailId) || null
    );
  }, [threadData?.emails, selectedEmailId]);

  // Get latest email (for initial display)
  const latestEmail = useMemo(() => {
    if (!threadData?.emails?.length) return null;
    return threadData.emails[threadData.emails.length - 1];
  }, [threadData?.emails]);

  // Automatically expand the latest email on first load or conversation change only.
  // Skip on refetch (e.g. signed-URL refresh) to avoid collapsing user-expanded emails.
  useEffect(() => {
    if (
      latestEmail &&
      initializedForConversationRef.current !== conversationId
    ) {
      initializedForConversationRef.current = conversationId;
      setExpandedEmails(new Set([latestEmail.id]));
    }
  }, [latestEmail, conversationId]);

  // Email selection handler
  const handleEmailSelect = (email: EmailThreadEmail) => {
    setSelectedEmailId(email.id);
    onEmailSelect?.(email);
  };

  // Email expansion handlers
  const toggleEmailExpanded = (emailId: string) => {
    setExpandedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const expandAllEmails = () => {
    if (!threadData?.emails) return;
    setExpandedEmails(new Set(threadData.emails.map((email) => email.id)));
  };

  const collapseAllEmails = () => {
    setExpandedEmails(new Set());
  };

  const toggleSplitDocuments = (emailId: string) => {
    setSplitDocsExpandedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  // Thread metadata
  const threadMetadata = useMemo(() => {
    if (!threadData) return null;

    return {
      totalCount: threadData.totalCount,
      participantEmails: threadData.participantEmails,
      earliestDate: new Date(threadData.earliestDate),
      latestDate: new Date(threadData.latestDate),
      conversationId: threadData.conversationId,
    };
  }, [threadData]);

  // All thread attachments count
  const totalAttachments = useMemo(() => {
    if (!threadData?.emails) return 0;
    return threadData.emails.reduce((total, email) => {
      return total + (email.attachmentCount || 0);
    }, 0);
  }, [threadData?.emails]);

  return {
    // Data
    threadData,
    selectedEmail,
    latestEmail,
    threadMetadata,
    totalAttachments,

    // State
    isLoading,
    isFetching,
    error,
    selectedEmailId,
    expandedEmails,
    splitDocsExpandedEmails: activeSplitDocsExpandedEmails,

    // Actions
    handleEmailSelect,
    toggleEmailExpanded,
    expandAllEmails,
    collapseAllEmails,
    toggleSplitDocuments,
    refetch,
  };
}
